'use client'

import React, { Suspense, memo, useMemo } from 'react'

import { useQuery } from '@tanstack/react-query'
import { twMerge } from 'tailwind-merge'

import {
  useRecipientAnalyticsQueries,
  useSuspenseRecipientDashboard,
} from '@/hooks/usaspending'

import type { SetAsideBreakdown } from '@/types/usaspending'

import { QueryErrorBoundary } from '../../DataErrorBoundary'
import { Currency, Percentage } from '../../Formatters'
import { SectionCard, SectionSkeleton, useDetailFilter } from '../../shared'
import { BarListUnavailable } from '../../shared/ServiceUnavailable'
import { useRecipientDetail } from '../context'

/**
 * RecipientDetail.SetAsides
 *
 * Displays set-aside program breakdown (Small Business, 8(a), HUBZone, etc.)
 * Shows percentage bars and award amounts.
 */
export function SetAsideSection() {
  const { recipientId } = useRecipientDetail()

  return (
    <SectionCard
      id="set-asides"
      title="Set-Aside Programs"
      animationDelay={250}
    >
      <QueryErrorBoundary
        title="Failed to load set-aside data"
        serviceUnavailableFallback={
          <BarListUnavailable height={200} bars={4} />
        }
      >
        <Suspense fallback={<SectionSkeleton height={200} bars={4} />}>
          <SetAsideContent id={recipientId} />
        </Suspense>
      </QueryErrorBoundary>
    </SectionCard>
  )
}

function SetAsideContent({ id }: { id: string }) {
  const { state } = useDetailFilter()
  const fiscalYear = state.fiscalYear === 'all' ? undefined : state.fiscalYear
  const { data: dashboard } = useSuspenseRecipientDashboard(id, fiscalYear)

  if (dashboard.setAsides === null) {
    return <DeferredSetAsides id={id} fiscalYear={fiscalYear} />
  }

  return <SetAsideList setAsides={dashboard.setAsides} />
}

function DeferredSetAsides({
  id,
  fiscalYear,
}: {
  id: string
  fiscalYear?: number
}) {
  const queries = useRecipientAnalyticsQueries()
  const { data, isLoading } = useQuery(queries.setAsides(id, fiscalYear))

  if (isLoading) return <SectionSkeleton height={200} bars={4} />
  if (!data) return null

  return <SetAsideList setAsides={data} />
}

function SetAsideList({ setAsides }: { setAsides: SetAsideBreakdown[] }) {
  const total = setAsides.reduce((sum, d) => sum + d.amount, 0)

  return (
    <div className="space-y-3">
      {setAsides.slice(0, 6).map((setAside, index) => {
        const percentage = total > 0 ? (setAside.amount / total) * 100 : 0
        return (
          <SetAsideRow
            key={setAside.typeCode || 'none'}
            setAside={setAside}
            percentage={percentage}
            index={index}
          />
        )
      })}
    </div>
  )
}

/**
 * Memoized row component to prevent unnecessary re-renders (rerender-memo)
 */
const SetAsideRow = memo(function SetAsideRow({
  setAside,
  percentage,
  index,
}: {
  setAside: SetAsideBreakdown
  percentage: number
  index: number
}) {
  // Memoize style object to avoid recreation (rerender-memo-with-default-value)
  const barStyle = useMemo(
    () => ({
      width: `${percentage}%`,
      animationDelay: `${index * 50}ms`,
    }),
    [percentage, index],
  )

  return (
    <div className="group w-full rounded-lg p-1 text-left transition-colors">
      <div className="mb-1 flex items-center justify-between text-sm">
        <span className="font-medium text-primary-800 dark:text-primary-200">
          {setAside.typeName || 'No Set-Aside'}
        </span>
        <span className="tabular-nums text-primary-500 dark:text-primary-400">
          <Percentage value={percentage} />
        </span>
      </div>
      <div className="relative h-2 w-full overflow-hidden rounded-full bg-primary-100 dark:bg-primary-800">
        <div
          className={twMerge(
            'absolute inset-y-0 left-0 rounded-full transition-all duration-500',
            index === 0
              ? 'bg-teal-500 dark:bg-teal-600'
              : index === 1
                ? 'bg-teal-400 dark:bg-teal-500'
                : 'bg-teal-300 dark:bg-teal-400',
          )}
          style={barStyle}
        />
      </div>
      <div className="mt-0.5 text-xs tabular-nums text-primary-400 dark:text-primary-500">
        <Currency value={setAside.amount} compact /> ·{' '}
        {setAside.awardCount.toLocaleString()} awards
      </div>
    </div>
  )
})
